/**
 * @papper/auth-notify
 *
 * Sends a one-time "user_activation" notification to the admin email
 * when a user first signs in to any papper app.
 *
 * Usage (in onAuthStateChange callback):
 *   import { notifyActivation } from '@papper/auth-notify'
 *   notifyActivation(supabase, 'reedle')
 */

const NOTIFY_URL = 'https://fhgxapmrciwlhsffdeyj.supabase.co/functions/v1/notify-signup'

// In-flight guard: prevents duplicate emails when onAuthStateChange fires
// INITIAL_SESSION + SIGNED_IN back-to-back during signup
let inflight = false

/**
 * Fire-and-forget activation notification.
 * Safe to call on every auth state change — idempotent via app_metadata.activation_notified
 * and client-side in-flight guard.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabaseClient
 * @param {string} appId - e.g. 'reedle', 'mellow', 'catto'
 */
export async function notifyActivation(supabaseClient, appId) {
  if (inflight) return
  try {
    const { data: { session } } = await supabaseClient.auth.getSession()
    if (!session?.user) return

    const user = session.user

    // Quick client-side check — skip network call if already notified for THIS app
    const notified = user.app_metadata?.activation_notified
    if (typeof notified === 'object' && notified !== null && notified[appId]) return
    // Backward compat: old boolean true means reedle was already notified
    if (typeof notified === 'boolean' && notified && appId === 'reedle') return

    // Skip anonymous users
    if (user.is_anonymous) return

    inflight = true

    fetch(NOTIFY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        type: 'user_activation',
        app_id: appId,
        record: {
          id: user.id,
          email: user.email,
          raw_user_meta_data: user.user_metadata,
          created_at: user.created_at,
        },
      }),
    }).catch((err) => {
      console.warn('[auth-notify] Activation notification failed (non-blocking):', err.message)
    }).finally(() => {
      inflight = false
    })
  } catch (err) {
    inflight = false
    console.warn('[auth-notify] notifyActivation error (non-blocking):', err.message)
  }
}
